import { z } from 'zod';
import {
  COMMUNITY_CATEGORIES,
  ID_PATTERN,
  LIMITS,
  MINECRAFT_EDIT_MAX_BYTES,
  MINECRAFT_SOFTWARE,
  MODERATION,
  MODERATION_RULES,
  NAME_FONTS,
  normalizeServerPath,
  RESERVED_USERNAMES,
  RESTRICTIONS,
  SLOWMODE_OPTIONS,
  SPACE_KINDS,
  SPACE_TAG_MAX,
  SPACE_TAG_PATTERN,
  STAFF_ROLES,
  SUPPORT,
  SUPPORT_CATEGORIES,
  TAG_BADGES,
} from './constants';

export const idSchema = z.string().regex(ID_PATTERN, 'Invalid id');

export const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(LIMITS.usernameMin, `Usernames need at least ${LIMITS.usernameMin} characters`)
  .max(LIMITS.usernameMax, `Usernames can have at most ${LIMITS.usernameMax} characters`)
  .regex(/^[a-z0-9_.]+$/, 'Use only letters, numbers, underscores and periods')
  .refine((v) => !v.includes('..'), 'Usernames cannot contain two periods in a row')
  .refine((v) => !(RESERVED_USERNAMES as readonly string[]).includes(v), 'This username is reserved');

export const displayNameSchema = z.string().trim().min(1, 'Display name is required').max(32);
export const passwordSchema = z
  .string()
  .min(LIMITS.passwordMin, `Passwords need at least ${LIMITS.passwordMin} characters`)
  .max(LIMITS.passwordMax);
export const emailSchema = z.string().trim().toLowerCase().max(254).email('Enter a valid email address');
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a #RRGGBB color');
const isoDate = z.iso.datetime({ offset: true });
const permissionBits = z.number().int().min(0).max(2 ** 31 - 1);

export const registerSchema = z.object({
  email: emailSchema,
  username: usernameSchema,
  displayName: displayNameSchema.optional(),
  password: passwordSchema,
  /** The sign-up checkbox: agreeing to the Terms of Service and Community Rules. */
  acceptTerms: z.literal(true, 'Agree to the Terms of Service and Community Rules to create an account'),
});

export const loginSchema = z.object({
  login: z.string().trim().min(1).max(254),
  password: z.string().min(1).max(LIMITS.passwordMax),
});

export const communityCategorySchema = z.enum(COMMUNITY_CATEGORIES, 'Pick what your community is about');

export const spaceCreateSchema = z
  .object({
    name: z.string().trim().min(2, 'Space names need at least 2 characters').max(100),
    description: z.string().trim().max(500).optional(),
    kind: z.enum(SPACE_KINDS).default('friends'),
    category: communityCategorySchema.optional(),
    /** Communities only: apply to be listed on Discover straight away. */
    submitToDiscover: z.boolean().default(false),
  })
  .superRefine((v, c) => {
    if (v.kind === 'community' && !v.category) c.addIssue({ code: 'custom', path: ['category'], message: 'Pick what your community is about' });
    if (v.kind === 'friends' && (v.category || v.submitToDiscover)) {
      c.addIssue({ code: 'custom', path: ['kind'], message: 'Friends spaces have no category and are never listed on Discover' });
    }
  });

export const spaceUpdateSchema = z.object({
  name: z.string().trim().min(2).max(100).optional(),
  description: z.string().trim().max(500).optional(),
  systemChannelId: idSchema.nullable().optional(),
  /** Turning a friends space into a community (or back) and picking its category. */
  kind: z.enum(SPACE_KINDS).optional(),
  category: communityCategorySchema.nullable().optional(),
});

export const spaceTagSchema = z.object({
  text: z
    .string()
    .trim()
    .min(1, 'Type a tag')
    .max(SPACE_TAG_MAX, `Tags can have at most ${SPACE_TAG_MAX} characters`)
    .regex(SPACE_TAG_PATTERN, 'Use letters, numbers and a few symbols like ! # & * - . / \\ ? @ _ ~, with at least one letter or number'),
  badge: z.enum(TAG_BADGES),
});

/** Which space tag to wear; null takes it off. */
export const userTagSchema = z.object({ spaceId: idSchema.nullable() });

export const discoverySubmitSchema = z.object({
  category: communityCategorySchema,
  /** What the community is about, shown on its Discover card. */
  description: z.string().trim().min(10, 'Tell people what your community is about (at least 10 characters)').max(500),
});

export const discoverQuerySchema = z.object({
  q: z.string().trim().max(100).default(''),
  category: z.enum(COMMUNITY_CATEGORIES).optional(),
  offset: z.coerce.number().int().min(0).max(10_000).default(0),
});

export const discoveryReviewSchema = z
  .object({
    decision: z.enum(['approve', 'deny']),
    /** Sent to the owner; required when denying so they know what to change. */
    note: z.string().trim().max(1000).default(''),
  })
  .refine((v) => v.decision === 'approve' || v.note.length >= 3, { path: ['note'], message: 'Tell the owner why it was denied' });

export const channelKindSchema = z.enum(['text', 'announcement', 'voice', 'stage', 'forum', 'media', 'category']);

export const channelCreateSchema = z.object({
  name: z.string().trim().min(1).max(100),
  type: channelKindSchema,
  parentId: idSchema.nullable().optional(),
  topic: z.string().trim().max(1024).optional(),
  private: z.boolean().optional(),
  /** Private channels: roles and members who can see it from the start. */
  allowRoleIds: z.array(idSchema).max(LIMITS.rolesPerSpace).optional(),
  allowUserIds: z.array(idSchema).max(50).optional(),
});

export const forumTagSchema = z.object({
  /** Omitted for a new tag; the server gives it an id. */
  id: idSchema.optional(),
  name: z.string().trim().min(1, 'Tags need a name').max(20, 'Tag names can have at most 20 characters'),
  emoji: z.string().trim().max(16).nullable().optional(),
  moderated: z.boolean().optional(),
});

export const channelUpdateSchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  topic: z.string().trim().max(1024).optional(),
  parentId: idSchema.nullable().optional(),
  slowmodeSeconds: z
    .number()
    .int()
    .refine((s) => (SLOWMODE_OPTIONS as readonly number[]).includes(s), 'Pick one of the slow mode options')
    .optional(),
  /** Forum and media channels. */
  availableTags: z.array(forumTagSchema).max(LIMITS.forumTags).optional(),
  defaultLayout: z.enum(['list', 'gallery']).optional(),
  requireTag: z.boolean().optional(),
  /** Threads only. */
  archived: z.boolean().optional(),
  /** Forum and media posts: the tags on the post. */
  appliedTags: z.array(idSchema).max(LIMITS.tagsPerPost).optional(),
});

export const forumPostCreateSchema = z.object({
  title: z.string().trim().min(1, 'Give your post a title').max(100),
  content: z.string().max(LIMITS.messageLength).default(''),
  attachmentIds: z.array(idSchema).max(LIMITS.attachmentsPerMessage).optional(),
  tagIds: z.array(idSchema).max(LIMITS.tagsPerPost).optional(),
});

export const forumPostQuerySchema = z.object({
  q: z.string().trim().max(100).default(''),
  tag: idSchema.optional(),
  sort: z.enum(['activity', 'created']).default('activity'),
  status: z.enum(['open', 'archived', 'all']).default('open'),
  limit: z.coerce.number().int().min(1).max(50).default(25),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});

export const spaceDeleteSchema = z.object({ name: z.string().max(100) });

/** Channels whose names are slugs (text, announcement, forum and media); voice, stage and categories keep any name. */
export function usesSlugName(type: string): boolean {
  return type === 'text' || type === 'announcement' || type === 'forum' || type === 'media';
}

/** Text channel names are lowercase slugs, like `release-notes`. */
export function slugifyChannelName(name: string): string {
  return (
    name
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, '-')
      .replace(/[^\p{L}\p{N}-]/gu, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 100) || 'channel'
  );
}

export const channelOrderSchema = z
  .array(z.object({ id: idSchema, position: z.number().int().min(0), parentId: idSchema.nullable().optional() }))
  .min(1)
  .max(LIMITS.channelsPerSpace);

export const overwriteSchema = z.object({
  targetType: z.enum(['role', 'member']),
  allow: permissionBits,
  deny: permissionBits,
});

export const roleCreateSchema = z.object({
  name: z.string().trim().min(1).max(100),
  color: hexColor.nullable().optional(),
  permissions: permissionBits.optional(),
  hoist: z.boolean().optional(),
  mentionable: z.boolean().optional(),
});

export const roleUpdateSchema = roleCreateSchema.partial();

export const roleOrderSchema = z
  .array(z.object({ id: idSchema, position: z.number().int().min(1) }))
  .min(1)
  .max(LIMITS.rolesPerSpace);

export const memberUpdateSchema = z.object({
  nickname: z.string().trim().max(32).nullable().optional(),
  roleIds: z.array(idSchema).max(LIMITS.rolesPerSpace).optional(),
  timeoutUntil: isoDate.nullable().optional(),
  reason: z.string().trim().max(512).optional(),
});

export const banSchema = z.object({
  reason: z.string().trim().max(512).optional(),
  deleteMessageHours: z.number().int().min(0).max(168).optional(),
});

export const reasonSchema = z.object({ reason: z.string().trim().max(512).optional() });

export const inviteCreateSchema = z.object({
  maxAgeSeconds: z.number().int().min(0).max(60 * 60 * 24 * 30).default(60 * 60 * 24 * 7),
  maxUses: z.number().int().min(0).max(1000).default(0),
});

export const messageCreateSchema = z.object({
  content: z.string().max(LIMITS.messageLength, `Messages can have at most ${LIMITS.messageLength} characters`).default(''),
  nonce: z.string().max(64).optional(),
  replyToId: idSchema.optional(),
  attachmentIds: z.array(idSchema).max(LIMITS.attachmentsPerMessage).optional(),
});

export const messageUpdateSchema = z.object({
  content: z.string().max(LIMITS.messageLength),
});

export const threadCreateSchema = z.object({
  name: z.string().trim().min(1).max(100),
});

export const messageQuerySchema = z.object({
  before: idSchema.optional(),
  after: idSchema.optional(),
  around: idSchema.optional(),
  limit: z.coerce.number().int().min(1).max(LIMITS.maxMessagePageSize).default(LIMITS.messagePageSize),
});

export const ackSchema = z.object({ messageId: idSchema });

/** An FCM registration token: opaque, URL-safe characters, around 150-200 long. */
export const pushTokenSchema = z
  .string()
  .min(32, 'Invalid push token')
  .max(4096, 'Invalid push token')
  .regex(/^[A-Za-z0-9_:.-]+$/, 'Invalid push token');

export const pushDeviceSchema = z.object({
  token: pushTokenSchema,
  platform: z.enum(['android']),
  appVersion: z.string().trim().max(32).optional(),
  previews: z.boolean().optional(),
});

export const pushDeviceUpdateSchema = z.object({ previews: z.boolean() });

export const relationshipCreateSchema = z.object({ username: usernameSchema });
export const relationshipPutSchema = z.object({ type: z.enum(['friend', 'blocked']) });

export const dmCreateSchema = z.union([
  z.object({ recipientId: idSchema }),
  z.object({
    recipientIds: z.array(idSchema).min(1).max(LIMITS.groupDmSize - 1),
    name: z.string().trim().max(100).optional(),
  }),
]);

export const groupDmUpdateSchema = z.object({ name: z.string().trim().max(100) });

export const profileUpdateSchema = z.object({
  displayName: displayNameSchema.optional(),
  bio: z.string().trim().max(190).optional(),
  pronouns: z.string().trim().max(40).optional(),
  bannerColor: hexColor.nullable().optional(),
  nameFont: z.enum(NAME_FONTS).nullable().optional(),
});

export const accountUpdateSchema = z.object({
  currentPassword: z.string().min(1).max(LIMITS.passwordMax),
  email: emailSchema.optional(),
  username: usernameSchema.optional(),
  newPassword: passwordSchema.optional(),
});

export const statusUpdateSchema = z.object({
  status: z.enum(['online', 'idle', 'dnd', 'invisible']).optional(),
  customStatus: z.string().trim().max(128).optional(),
});

const nullableDeviceId = z.string().max(256).nullable();

export const settingsUpdateSchema = z
  .object({
    theme: z.enum(['system', 'light', 'dark']),
    density: z.enum(['cozy', 'compact']),
    fontScale: z.number().int().min(80).max(150),
    use24Hour: z.boolean(),
    reducedMotion: z.boolean(),
    highContrast: z.boolean(),
    underlineLinks: z.boolean(),
    showEmbeds: z.boolean(),
    desktopNotifications: z.boolean(),
    notificationSounds: z.boolean(),
    dmPolicy: z.enum(['everyone', 'space_members', 'friends']),
    friendRequestPolicy: z.enum(['everyone', 'mutual', 'nobody']),
    showBlockedMessages: z.boolean(),
    spaceOrder: z.array(idSchema).max(LIMITS.spacesPerUser),
    audio: z
      .object({
        inputDeviceId: nullableDeviceId,
        outputDeviceId: nullableDeviceId,
        videoDeviceId: nullableDeviceId,
        echoCancellation: z.boolean(),
        noiseSuppression: z.boolean(),
        autoGainControl: z.boolean(),
        inputVolume: z.number().int().min(0).max(200),
        outputVolume: z.number().int().min(0).max(200),
        speakingThreshold: z.number().int().min(0).max(100),
      })
      .partial(),
  })
  .partial();

export const notificationSettingSchema = z.object({
  level: z.enum(['all', 'mentions', 'nothing']).nullable().optional(),
  mutedUntil: isoDate.nullable().optional(),
  suppressEveryone: z.boolean().optional(),
});

export const reportCreateSchema = z
  .object({
    category: z.enum(['spam', 'harassment', 'hate', 'violence', 'self_harm', 'sexual', 'other']),
    reason: z.string().trim().max(1000).default(''),
    messageId: idSchema.optional(),
    userId: idSchema.optional(),
    spaceId: idSchema.optional(),
  })
  .refine((r) => r.messageId || r.userId, 'Report a message or a user');

export const reportUpdateSchema = z.object({
  status: z.enum(['resolved', 'dismissed', 'open']),
  note: z.string().trim().max(1000).optional(),
});

// ---- Platform moderation ----------------------------------------------------

export const moderationActionCreateSchema = z
  .object({
    type: z.enum(['warning', 'ban', 'restriction']),
    restriction: z.enum(RESTRICTIONS).nullable().optional(),
    rule: z.enum(MODERATION_RULES),
    reason: z.string().trim().min(3, 'Explain the reason; the person sees it').max(1000),
    staffNote: z.string().trim().max(2000).default(''),
    /** How long a ban or restriction lasts; null means permanent. Ignored for warnings. */
    durationHours: z.number().int().min(1).max(MODERATION.maxHours).nullable().default(null),
    reportId: idSchema.nullable().optional(),
  })
  .refine((a) => a.type !== 'restriction' || !!a.restriction, { message: 'Pick what to restrict', path: ['restriction'] });

export const moderationRevokeSchema = z.object({ reason: z.string().trim().min(3, 'Give a reason for lifting it').max(1000) });

export const appealCreateSchema = z.object({
  message: z.string().trim().min(10, 'Tell us a bit more (at least 10 characters)').max(2000),
});

export const appealReviewSchema = z.object({
  decision: z.enum(['accept', 'deny']),
  response: z.string().trim().min(3, 'Write a short reply; the person sees it').max(2000),
});

export const staffRoleSchema = z.object({ role: z.enum(STAFF_ROLES).nullable() });

export const adminUserQuerySchema = z.object({
  q: z.string().trim().max(100).default(''),
  filter: z.enum(['all', 'staff', 'banned', 'restricted']).default('all'),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});

export const adminListQuerySchema = z.object({
  q: z.string().trim().max(100).default(''),
  status: z.enum(['open', 'resolved', 'dismissed', 'accepted', 'denied', 'pending', 'approved', 'withdrawn', 'all']).default('open'),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});

export const adminReasonSchema = z.object({ reason: z.string().trim().min(3, 'Give a reason').max(1000) });

export const searchQuerySchema = z.object({
  q: z.string().trim().max(200).default(''),
  spaceId: idSchema.optional(),
  channelId: idSchema.optional(),
  authorId: idSchema.optional(),
  has: z.enum(['file', 'image', 'link']).optional(),
  mentions: idSchema.optional(),
  pinned: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  limit: z.coerce.number().int().min(1).max(50).default(25),
  offset: z.coerce.number().int().min(0).max(5000).default(0),
});

export const transferOwnershipSchema = z.object({
  userId: idSchema,
  password: z.string().min(1).max(LIMITS.passwordMax),
});

export const passwordConfirmSchema = z.object({ password: z.string().min(1).max(LIMITS.passwordMax) });

export const auditQuerySchema = z.object({
  before: idSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  action: z.string().max(40).optional(),
  actorId: idSchema.optional(),
});

export const memberQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(1000),
});

export type RegisterInput = z.infer<typeof registerSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type ChannelCreateInput = z.infer<typeof channelCreateSchema>;
export type MessageCreateInput = z.infer<typeof messageCreateSchema>;
export type SettingsUpdateInput = z.infer<typeof settingsUpdateSchema>;
export type SearchQuery = z.infer<typeof searchQuerySchema>;

// ---- Minecraft hosting ------------------------------------------------------

/** Minecraft version ids like 1.21.4, 26.3 or 1.20.5-rc1; the server checks they exist for the software. */
export const minecraftVersionSchema = z
  .string()
  .trim()
  .regex(/^[0-9][0-9A-Za-z.-]{1,19}$/, 'Enter a Minecraft version like 1.21.4');

export const minecraftCreateSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Give your server a name')
    .max(32, 'Server names can have at most 32 characters')
    .regex(/^[\p{L}\p{N} _'.!-]+$/u, 'Use letters, numbers, spaces and simple punctuation'),
  software: z.enum(MINECRAFT_SOFTWARE),
  /** null or omitted means the latest version. */
  version: minecraftVersionSchema.nullable().optional(),
  acceptEula: z.literal(true, 'You need to accept the Minecraft EULA'),
});

export const minecraftPowerSchema = z.object({ action: z.enum(['start', 'stop', 'restart']) });

export const minecraftCommandSchema = z.object({
  command: z
    .string()
    .trim()
    .min(1, 'Enter a command')
    .max(256)
    .regex(/^[^\r\n]+$/, 'Commands must be on one line'),
});

export const minecraftVersionsQuerySchema = z.object({ software: z.enum(MINECRAFT_SOFTWARE) });

/** A path inside a hosted server, cleaned to "/a/b"; "/" is the server's own folder. */
export const minecraftPathSchema = z
  .string()
  .max(1024, 'That path is too long')
  .refine((p) => normalizeServerPath(p) !== null, 'That is not a valid path')
  .transform((p) => normalizeServerPath(p)!);

/** A path to a file or folder, which can't be the server folder itself. */
export const minecraftFilePathSchema = minecraftPathSchema.refine((p) => p !== '/', 'Pick a file');

export const minecraftFileNameSchema = z
  .string()
  .trim()
  .min(1, 'Enter a name')
  .max(255, 'Names can have at most 255 characters')
  // eslint-disable-next-line no-control-regex
  .refine((n) => n !== '.' && n !== '..' && !/[/\\"\u0000-\u001f\u007f]/.test(n), 'Names cannot contain / \\ or "');

export const minecraftDirQuerySchema = z.object({ dir: minecraftPathSchema.default('/') });
export const minecraftFileQuerySchema = z.object({ file: minecraftFilePathSchema });

export const minecraftWriteFileSchema = z.object({
  file: minecraftFilePathSchema,
  content: z.string().max(MINECRAFT_EDIT_MAX_BYTES, 'Files in the editor can be at most 1 MB'),
});

export const minecraftRenameSchema = z.object({ from: minecraftFilePathSchema, to: minecraftFilePathSchema });

export const minecraftDeleteFilesSchema = z.object({
  root: minecraftPathSchema,
  files: z.array(minecraftFileNameSchema).min(1, 'Pick something to delete').max(100),
});

export const minecraftCreateFolderSchema = z.object({ root: minecraftPathSchema, name: minecraftFileNameSchema });

export const minecraftUploadQuerySchema = z.object({ dir: minecraftPathSchema.default('/') });

// ---- Support tickets ----------------------------------------------------------

const supportMessageSchema = z.string().trim().max(SUPPORT.messageMax, `Messages can have at most ${SUPPORT.messageMax} characters`);

export const supportTicketCreateSchema = z.object({
  category: z.enum(SUPPORT_CATEGORIES, 'Pick what you need help with'),
  subject: z.string().trim().min(3, 'Add a short subject').max(SUPPORT.subjectMax, `Subjects can have at most ${SUPPORT.subjectMax} characters`),
  message: supportMessageSchema.min(10, 'Tell us a bit more (at least 10 characters)'),
  /** Signed out only: the email your account is registered with, so staff can find it. */
  email: emailSchema.optional(),
  /** Signed in only: one of your hosted game servers. */
  serverId: idSchema.nullable().optional(),
});

export const supportReplySchema = z.object({ message: supportMessageSchema.min(1, 'Write a message') });

export const adminSupportReplySchema = supportReplySchema.extend({
  /** Close the ticket with this reply. */
  close: z.boolean().default(false),
});

export const adminSupportStatusSchema = z.object({ status: z.enum(['open', 'closed']) });

export const adminSupportQuerySchema = z.object({
  q: z.string().trim().max(100).default(''),
  status: z.enum(['open', 'answered', 'closed', 'all']).default('open'),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});
